import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

export const TestPage: React.FC = () => {
  const { profile, isAuthenticated, login, logout } = useAuth();
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testAuthentication = async () => {
    setIsLoading(true);
    setTestResults([]);
    
    try {
      // Test 1: Check if user is authenticated
      addResult(`Authentication Status: ${isAuthenticated ? 'AUTHENTICATED' : 'NOT AUTHENTICATED'}`);
      
      // Test 2: Check profile
      addResult(`Profile: ${profile ? `${profile.firstName} ${profile.lastName} (${profile.email})` : 'NO PROFILE'}`);
      
      // Test 3: Test token verification
      try {
        const tokenResult = await apiService.verifyToken();
        addResult(`Token Verification: SUCCESS - ${JSON.stringify(tokenResult)}`);
      } catch (error) {
        addResult(`Token Verification: FAILED - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      
      // Test 4: Test profile fetch
      try {
        const profileResult = await apiService.getProfile();
        addResult(`Profile Fetch: SUCCESS - ${profileResult.firstName} ${profileResult.lastName}`);
      } catch (error) {
        addResult(`Profile Fetch: FAILED - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      
    } catch (error) {
      addResult(`Test Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testLogin = async () => {
    setIsLoading(true);
    try {
      await login('test@kairos.dev', 'test123');
      addResult('Login Test: SUCCESS');
    } catch (error) {
      addResult(`Login Test: FAILED - ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
      addResult('Logout Test: SUCCESS');
    } catch (error) {
      addResult(`Logout Test: FAILED - ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>🔧 Kairos Authentication Test Page</h1>
      <p>This page helps debug authentication issues in the AWS environment.</p>
      
      <div style={{ marginBottom: '20px' }}>
        <h2>Current Status</h2>
        <p><strong>Authenticated:</strong> {isAuthenticated ? '✅ YES' : '❌ NO'}</p>
        <p><strong>Profile:</strong> {profile ? `${profile.firstName} ${profile.lastName}` : 'None'}</p>
        <p><strong>Email:</strong> {profile?.email || 'None'}</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2>Test Actions</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={testAuthentication}
            disabled={isLoading}
            style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px' }}
          >
            {isLoading ? 'Testing...' : 'Test Authentication'}
          </button>
          
          <button 
            onClick={testLogin}
            disabled={isLoading}
            style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px' }}
          >
            {isLoading ? 'Logging in...' : 'Test Login'}
          </button>
          
          <button 
            onClick={testLogout}
            disabled={isLoading}
            style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px' }}
          >
            {isLoading ? 'Logging out...' : 'Test Logout'}
          </button>
        </div>
      </div>

      <div>
        <h2>Test Results</h2>
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '5px', 
          padding: '15px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {testResults.length === 0 ? (
            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>No tests run yet. Click a test button above.</p>
          ) : (
            testResults.map((result, index) => (
              <div key={`test-result-${index}`} style={{ marginBottom: '5px', fontFamily: 'monospace', fontSize: '14px' }}>
                {result}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '5px' }}>
        <h3>Authentication Required</h3>
        <p>Please register a new account or contact your administrator for access.</p>
      </div>
    </div>
  );
};
